import React, { useState, useCallback } from 'react'
import AutoResizeTextarea from '../../shared/AutoResizeTextarea'
import tocflVocabulary from '../../../data/tocfl_vocabulary.json'

/**
 * Input Methods Component
 * Supports two ways to input characters:
 * 1. Direct text input (with manual extract button)
 * 2. TOCFL level selection (random vocabulary by level)
 */
const InputMethods = ({ onCharactersChange, translations = {} }) => {
  const t = (key, defaultValue) => translations[key] || defaultValue

  const [activeTab, setActiveTab] = useState('direct')
  const [directInput, setDirectInput] = useState('')
  const [tocflLevel, setTocflLevel] = useState('A1')
  const [selectedCount, setSelectedCount] = useState(0)

  // Fisher-Yates shuffle algorithm for random selection
  const shuffleArray = (array) => {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }

  // Build a lookup map from all TOCFL vocabulary for quick character lookup
  const vocabularyLookup = React.useMemo(() => {
    const lookup = new Map()
    // Iterate through all levels (A1, A2, B1, B2, C1) and build lookup
    Object.values(tocflVocabulary).forEach(levelVocab => {
      levelVocab.forEach(entry => {
        if (!lookup.has(entry.character)) {
          lookup.set(entry.character, entry)
        }
      })
    })
    return lookup
  }, [])

  // Look up character data from vocabulary, return with zhuyin/pinyin/english if found
  const lookupCharacter = useCallback((char) => {
    const found = vocabularyLookup.get(char)
    if (found) {
      return found
    }
    // Return basic object if not found in vocabulary
    return { character: char, zhuyin: '', pinyin: '', english: '' }
  }, [vocabularyLookup])

  // Parse Chinese characters from text
  const extractCharacters = useCallback((text) => {
    // Match Chinese characters (CJK Unified Ideographs)
    const chineseRegex = /[\u4e00-\u9fff]/g
    const matches = text.match(chineseRegex) || []
    // Remove duplicates while preserving order
    return [...new Set(matches)]
  }, [])

  // Handle direct input change (only update state, no extraction)
  const handleDirectInputChange = useCallback((e) => {
    setDirectInput(e.target.value)
  }, [])

  // Handle extract button click
  const handleExtractCharacters = useCallback(() => {
    const chars = extractCharacters(directInput)
    // Look up each character in vocabulary to get zhuyin/pinyin/english
    onCharactersChange(chars.map(char => lookupCharacter(char)))
  }, [directInput, extractCharacters, onCharactersChange, lookupCharacter])

  // Generate random characters from TOCFL vocabulary
  const generateRandomChars = useCallback((level, count = 10) => {
    const levelVocab = tocflVocabulary[level] || []
    const shuffled = shuffleArray(levelVocab)
    const selected = shuffled.slice(0, count)
    setSelectedCount(selected.length)
    onCharactersChange(selected)
  }, [onCharactersChange])

  // Handle TOCFL level selection
  const handleTocflLevelChange = useCallback((level) => {
    setTocflLevel(level)
    generateRandomChars(level)
  }, [generateRandomChars])

  // Handle regenerate button click
  const handleRegenerate = useCallback(() => {
    generateRandomChars(tocflLevel)
  }, [tocflLevel, generateRandomChars])

  const tabs = [
    { id: 'direct', label: t('tab_direct', 'Direct Input') },
    { id: 'tocfl', label: t('tab_tocfl', 'TOCFL Level') }
  ]

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-[#3366FF] text-[#3366FF]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[200px]">
        {/* Direct Input */}
        {activeTab === 'direct' && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              {t('direct_label', 'Enter Chinese characters')}
            </label>
            <AutoResizeTextarea
              value={directInput}
              onChange={handleDirectInputChange}
              placeholder={t('direct_placeholder', 'Enter or paste text containing Chinese characters...')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3366FF] focus:border-transparent resize-none"
              rows={5}
            />
            <button
              onClick={handleExtractCharacters}
              disabled={!directInput.trim()}
              className="px-4 py-2 text-sm font-medium text-[#3366FF] border border-[#3366FF] rounded-lg hover:bg-[#E6F0FF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('extract_button', 'Extract Characters')}
            </button>
            <p className="text-xs text-gray-500">
              {t('direct_hint', 'Tip: Enter or paste text, then click the button to extract Chinese characters.')}
            </p>
          </div>
        )}

        {/* TOCFL Level Selection */}
        {activeTab === 'tocfl' && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('tocfl_label', 'Select TOCFL Level')}
            </label>
            <div className="grid grid-cols-5 gap-2">
              {['A1', 'A2', 'B1', 'B2', 'C1'].map(level => (
                <button
                  key={level}
                  onClick={() => handleTocflLevelChange(level)}
                  className={`py-2 px-3 rounded-lg font-medium text-sm transition-colors ${
                    tocflLevel === level
                      ? 'bg-[#3366FF] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              {t('tocfl_hint', 'Select a level to randomly generate vocabulary from the TOCFL word list.')}
            </p>

            {/* Regenerate button and count display */}
            {selectedCount > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-sm text-blue-800">
                  {t('tocfl_selected_count', '%{count} characters selected').replace('%{count}', selectedCount)}
                </span>
                <button
                  onClick={handleRegenerate}
                  className="px-3 py-1.5 text-sm font-medium text-[#3366FF] border border-[#3366FF] rounded-lg hover:bg-[#E6F0FF] transition-colors"
                >
                  {t('tocfl_regenerate', 'Regenerate')}
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  )
}

export default InputMethods
